import { Divider, Loading, Popover } from '@byecode/ui'
import type { Field } from '@lighthouse/core'
import { useDebounce } from '@lighthouse/tools'
import type { FloatingPosition } from '@mantine/core/lib/Floating'
import { find, max, min } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSetState, useUpdateEffect } from 'react-use'

import { USER_DATASOURCE } from '../../../../constants'
import { useFieldBlockContext } from '../../../../contexts'
import { useAtomData } from '../../../../hooks'
import { useAppThemeContext } from '../../../ApplicationContainer'
import FieldTable from '../../../FiledSelectTable'
import { applyFillPickerValue4CssColorValue } from '../../../FillPicker'
import { outlineWidth, popoverMaxHeight } from '../../constant'
import { AddButton } from './AddButton'
import FieldList from './FieldList'
import { RelationRecordPreview } from './RelationRecordPreview'
import { SearchInput } from './SearchInput'
import * as SC from './styles'
import type { RelationRecordSelectProps, RelationRecordSelectState } from './types'
import { useRelativeDatasource } from './useRelativeDatasource'

export const RecordRecordSelect = React.forwardRef<HTMLDivElement, RelationRecordSelectProps>(
    (
        {
            id,
            tableWidth,
            dsId,
            dataSource,
            value: relationValue = [],
            userRecord,
            relativeField,
            relativeShowField,
            relativeSelectConfig,
            readOnly,
            opened,
            containerRef,
            containerRect,
            dataSourceList,
            breakPoint,
            onAddClickEvent,
            onChangeOpened,
            onChange,
            onLoadMoreData,
            onFetchDataSource
        },
        ref
    ) => {
        const { stackId, viewIndependentDataAtomFamily } = useFieldBlockContext()
        const { theme } = useAppThemeContext()
        const { viewOptions } = dataSource ?? {}
        const { pagination, tableProps: fieldProps = [] } = viewOptions ?? {}
        const { placeholderColor, fontFamily } = useMemo(
            () => ({
                placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
                fontFamily: breakPoint?.font?.fontFamily
            }),
            [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
        )

        const { rowTotal, currentPage = 1, recordPoints } = useAtomData(viewIndependentDataAtomFamily({ stackId, viewId: id, dsId }))
        const { relativeSelect, placeholder } = relativeSelectConfig ?? {}
        const { addButton, showType = 'table', canMultipleChoice } = relativeSelect ?? {}
        const { enable: addButtonEnable } = addButton ?? {}

        const tableContentRef = useRef<HTMLDivElement>(null)

        const scrollRef = useRef<HTMLDivElement>(null)

        const [state, setState] = useSetState<RelationRecordSelectState>({
            isLookSelected: false
            // selectList: relationValue
        })
        const { isLookSelected } = state

        const userDataSource = useMemo(() => {
            return find(item => item.id === USER_DATASOURCE, dataSourceList || [])
        }, [dataSourceList])

        const {
            state: { loading, keywords, initLoading },
            handleFetchData,
            handleChangeSearch,
            handleLoadMoreData,
            handleChangeInitLoading
        } = useRelativeDatasource({ id, onFetchDataSource, onLoadMoreData, dsId })

        const filterRecordPoints = useMemo(() => {
            if (isLookSelected) {
                return recordPoints?.filter(point => relationValue.includes(point.id))
            }
            return recordPoints
        }, [isLookSelected, recordPoints, relationValue])

        const filterRowTotal = useMemo(() => {
            if (!rowTotal) {
                return 0
            }
            return rowTotal
        }, [rowTotal])
        // const options = useMemo(() => filterRecordIds, [filterRecordIds])

        const columns = useMemo(() => {
            if (!dataSource) {
                return []
            }
            const { schema } = dataSource
            if (fieldProps && fieldProps.length > 0) {
                return fieldProps.reduce<Field[]>((cols, col) => {
                    if (schema[col.id] && col.visible) {
                        cols.push(schema[col.id])
                    }
                    return cols
                }, [])
            }
            return Object.entries(schema).reduce<Field[]>((cols, [key, col]) => {
                cols.push({ ...col })
                return cols
            }, [])
        }, [dataSource, fieldProps])

        const inputWidth = useMemo(() => containerRect?.width ?? 380, [containerRect?.width])

        const popoverWidth = useMemo(() => (inputWidth < 380 ? 380 : inputWidth) + outlineWidth * 2, [inputWidth])

        // const selectListRef = useLatest(relationValue)
        // useUpdateEffect(() => {
        //     if (opened) {
        //         setState({ selectList: selectListRef.current })
        //     }
        // }, [opened])

        const handleRemove = useCallback(
            (recordId: string) => {
                const newValue = relationValue.filter(item => item !== recordId)
                // setState({ selectList: newValue })
                return onChange?.(newValue)
            },
            [onChange, relationValue]
        )

        const [position, tableHeight]: [FloatingPosition, number] = useMemo(() => {
            if (!containerRef || !containerRef.current || (!containerRef?.current && !opened)) {
                return ['bottom-start', popoverMaxHeight]
            }
            const containRect = containerRef?.current?.getBoundingClientRect()
            const top = containRect.y
            const bottom = window.innerHeight - containRect.y - containRect.height
            const maxHeight = max(top, bottom)
            const usedHeight = max(50, maxHeight - 150)
            const position: FloatingPosition = top > bottom ? 'top-start' : 'bottom-start'
            return [position, min(popoverMaxHeight, usedHeight)]
        }, [containerRef, opened])

        const handleScrollPositionChange = useCallback(
            (position: { x: number; y: number }) => {
                const { y } = position
                if (!scrollRef.current) {
                    return
                }
                const { clientHeight, scrollHeight } = scrollRef.current
                const isBottom = y + clientHeight + 100 > scrollHeight

                if (isBottom && !loading && filterRecordPoints?.length && filterRowTotal > filterRecordPoints.length) {
                    handleLoadMoreData(currentPage + 1)
                }
            },
            [currentPage, filterRecordPoints?.length, filterRowTotal, handleLoadMoreData, loading]
        )

        const debouncedKeywords = useDebounce(keywords, 500)

        useUpdateEffect(() => {
            if (debouncedKeywords !== '') {
                handleFetchData(debouncedKeywords)
            }
        }, [debouncedKeywords])

        const invokeChange = useCallback(
            (val: string[]) => {
                onChange?.(val)
            },
            [onChange]
        )

        const handleItemClick = useCallback(
            (value: string) => {
                // const isExists = relationValue.includes(value)
                // let newValue: string[] = []
                if (canMultipleChoice) {
                    const isExistRecordId = relationValue.includes(value)
                    const newValue = isExistRecordId ? relationValue.filter(item => item !== value) : [...relationValue, value]
                    invokeChange(newValue)
                    // setState(({ selectList }) => {
                    //     const isExistRecordId = relationValue.includes(value)
                    //     return { selectList: newValue }
                    // })
                    return
                }
                // newValue = isExists ? [] : [value]
                const isExistRecordId = relationValue.includes(value)
                const newValue = isExistRecordId ? [] : [value]
                invokeChange(newValue)
                // invokeChange(newValue)
                onChangeOpened(false)
            },
            [canMultipleChoice, invokeChange, onChangeOpened, relationValue]
        )

        const handleSearchValueChange = useCallback(
            (label: string) => {
                const search = label.trim()
                if (search === keywords) {
                    return
                }
                handleChangeSearch(search)
            },
            [handleChangeSearch, keywords]
        )

        useUpdateEffect(() => {
            if (opened) {
                handleChangeInitLoading(true)
                handleFetchData?.()
            }
        }, [opened])

        return (
            <Popover
                strategy="fixed"
                withinPortal
                opened={opened}
                width={popoverWidth}
                onChange={onChangeOpened}
                position={position}
                positionDependencies={[filterRecordPoints]}
                closeOnEscape
                disabled={readOnly || !relativeField || !relativeShowField}
            >
                <Popover.Target>
                    <div>
                        <RelationRecordPreview
                            dsId={dsId}
                            relativeField={relativeField}
                            relativeShowField={relativeShowField}
                            value={relationValue}
                            readOnly={readOnly}
                            userRecord={userRecord}
                            userDataSource={userDataSource}
                            onRemove={handleRemove}
                            placeholder={placeholder}
                            placeholderColor={placeholderColor}
                        />
                    </div>
                </Popover.Target>
                <Popover.Dropdown compact>
                    <SC.RecordSelectWrapper data-type="fl-node-part" style={{ fontFamily }} ref={ref}>
                        <SearchInput
                            showType={showType}
                            value={keywords}
                            switchValue={isLookSelected}
                            // autoFocus
                            style={{ padding: 12 }}
                            onChange={handleSearchValueChange}
                            onChangeSwitch={val => setState({ isLookSelected: val })}
                        />
                        {initLoading ? (
                            <Loading />
                        ) : (
                            <>
                                {showType === 'table' ? (
                                    <SC.RecordSelector ref={tableContentRef}>
                                        <FieldTable
                                            dsId={dsId}
                                            recordPoints={filterRecordPoints}
                                            ref={scrollRef}
                                            tableWidth={tableWidth}
                                            tableHeight={tableHeight}
                                            value={relationValue}
                                            columns={columns}
                                            loading={loading}
                                            primaryField="ID"
                                            dataSourceList={dataSourceList}
                                            onRowClick={handleItemClick}
                                            onScrollPositionChange={handleScrollPositionChange}
                                        />
                                    </SC.RecordSelector>
                                ) : (
                                    <FieldList
                                        recordPoints={filterRecordPoints}
                                        value={relationValue}
                                        ref={scrollRef}
                                        dataSource={dataSource}
                                        tableWidth={tableWidth}
                                        tableHeight={tableHeight}
                                        onScrollPositionChange={handleScrollPositionChange}
                                        onChange={handleItemClick}
                                    />
                                )}
                            </>
                        )}
                        {addButtonEnable && addButton && (
                            <>
                                <Divider color="var(--color-gray-200)" />
                                <AddButton
                                    id={id}
                                    config={addButton}
                                    style={{ justifyContent: showType === 'table' ? 'center' : 'flex-start' }}
                                    isHover
                                    onClose={() => onChangeOpened(false)}
                                    onClickEvent={onAddClickEvent}
                                />
                            </>
                        )}
                    </SC.RecordSelectWrapper>
                </Popover.Dropdown>
            </Popover>
        )
    }
)
